import React, { useEffect, useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Button,
  Grid,
  Divider,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import MyProductList from './MyProductList';

const ResellerProfile = () => {
  const [reseller, setReseller] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    profileImage: null,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const stored = localStorage.getItem('reseller');
    if (stored) {
      const parsed = JSON.parse(stored);
      setReseller(parsed);
      setFormData({
        name: parsed.name,
        bio: parsed.bio || '',
        profileImage: null,
      });

      fetch(`https://reviewhub-lc.onrender.com/api/reviews/reseller/${parsed.resellerId}`)
        .then((res) => res.json())
        .then((reviews) => {
          setReseller((prev) => ({ ...prev, reviews }));
        })
        .catch((err) => console.error('Error fetching reviews:', err));

      axios
        .get(`https://reviewhub-lc.onrender.com/api/auth/reseller-followers/${parsed.resellerId}`)
        .then((res) => {
          setFollowerCount(res.data.followerCount || 0);
        })
        .catch((err) => console.error('Error fetching follower count:', err));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('bio', formData.bio);
      if (formData.profileImage) {
        data.append('profileImage', formData.profileImage);
      }

      const res = await axios.patch(
        `https://reviewhub-lc.onrender.com/api/resellers/${reseller._id}/update`,
        data
      );

      const updatedReseller = res.data;
      setReseller(updatedReseller);
      localStorage.setItem('reseller', JSON.stringify(updatedReseller));
      setEditMode(false);
      alert('Profile updated successfully!');
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update profile. Please try again.');
    }
  };

  if (!reseller) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ p: isMobile ? 1 : 4 }}>
      {/* Top Section: Image + ID, Name, Counts */}
      <Grid container spacing={2} alignItems="center">
        {/* Profile Image */}
        <Grid item>
          <Avatar
            src={
              formData.profileImage
                ? URL.createObjectURL(formData.profileImage)
                : reseller.profileImageUrl || '/assets/defaultProfile.jpg'
            }
            alt="Profile"
            sx={{
              width: isMobile ? 60 : 90,
              height: isMobile ? 60 : 90,
            }}
          />
          {editMode && (
            <Button component="label" variant="text" size="small" sx={{ mt: 1 }}>
              Change
              <input
                type="file"
                name="profileImage"
                hidden
                accept="image/*"
                onChange={handleChange}
              />
            </Button>
          )}
        </Grid>

        {/* ID, Name, Counts */}
        <Grid item xs>
          {!editMode ? (
            <>
              <Typography variant="h6" fontWeight="bold">
                {reseller.resellerId}
              </Typography>
              <Typography variant="subtitle1">{reseller.name}</Typography>

              <Box display="flex" gap={3} mt={1} flexWrap="wrap">
                <Typography>
                  <strong>{reseller.reviews?.length || 0}</strong> Reviews
                </Typography>
                <Typography>
                  <strong>{followerCount}</strong> Followers
                </Typography>
                {/* <Typography>
                  <strong>{reseller.following || 0}</strong> Following
                </Typography> */}
              </Box>

              <Button
                variant="outlined"
                size="small"
                sx={{ mt: 1, textTransform: 'none' }}
                onClick={() => setEditMode(true)}
              >
                Edit Profile
              </Button>
            </>
          ) : (
            <>
              <TextField
                fullWidth
                name="name"
                label="Name"
                value={formData.name}
                onChange={handleChange}
                sx={{ mb: 1 }}
              />
            </>
          )}
        </Grid>
      </Grid>

      {/* Bio Full Width Below */}
      <Box sx={{ mt: 2 }}>
        {!editMode ? (
          <Typography
            variant="body1"
            sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}
          >
            {reseller.bio || 'No bio added yet.'}
          </Typography>
        ) : (
          <TextField
            fullWidth
            name="bio"
            label="Bio"
            value={formData.bio}
            onChange={handleChange}
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />
        )}

        {editMode && (
          <Box display="flex" gap={2}>
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
            <Button variant="outlined" onClick={() => setEditMode(false)}>
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Reviews Section */}
      <Typography variant="h6" gutterBottom>
        Reviews
      </Typography>
      <Box mt={2}>
        <MyProductList />
      </Box>
    </Box>
  );
};

export default ResellerProfile;
